import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import MissingWarehouseStockDialog from './MissingWarehouseStockDialog';
import {
  createProductsInWarehouse,
  findProductsMissingInWarehouse,
  type MissingWarehouseProduct,
} from '../utils/warehouseStockProvisioning';

/**
 * Yanlış depoya kurgulanmış bir sekmeyi, kategori/ürün seçimlerini kaybetmeden
 * başka bir depoya taşır.
 *
 * NEDEN: Eskiden tek yol "yanlış depoyu sil, doğrusunu ekle"ydi; bu da onlarca
 * kategori/ürün seçimini elle yeniden yapmak anlamına geliyordu. Burada sadece
 * seçimlerin anahtarı değişiyor.
 *
 * Taşıma sonrası, taslak uygulama akışıyla AYNI kontrol yapılıyor: ürünlerin
 * hedef depoda stok satırı yoksa 1 adetlik tanım açma teklif edilir. Kaynak
 * depodan miktar düşülmez.
 */

export interface WarehouseOption {
  id: string;
  name: string;
}

interface WarehouseChangeDialogProps {
  open: boolean;
  companyId: string | null | undefined;
  /** Değiştirilecek (mevcut) depo. */
  sourceWarehouse: WarehouseOption | null;
  allWarehouses: WarehouseOption[];
  /** Ekranda seçili depolar — bunlar hedef olarak seçilemez (çakışma olurdu). */
  selectedWarehouseIds: string[];
  /** Kaynak depodaki kategori sayısı (bilgi amaçlı). */
  categoryCount: number;
  /** Kaynak depodaki tüm ürün id'leri — eksik tanım kontrolü için. */
  productIds: string[];
  onClose: () => void;
  /** Seçimleri hedef depoya taşır (state güncellemesi çağıranda yapılır). */
  onConfirm: (sourceWarehouseId: string, targetWarehouseId: string) => void;
}

const WarehouseChangeDialog: React.FC<WarehouseChangeDialogProps> = ({
  open,
  companyId,
  sourceWarehouse,
  allWarehouses,
  selectedWarehouseIds,
  categoryCount,
  productIds,
  onClose,
  onConfirm,
}) => {
  const [targetWarehouse, setTargetWarehouse] = useState<WarehouseOption | null>(null);
  const [changing, setChanging] = useState(false);

  const [missingProducts, setMissingProducts] = useState<MissingWarehouseProduct[] | null>(null);
  const [missingWarehouse, setMissingWarehouse] = useState<WarehouseOption | null>(null);
  const [creatingStocks, setCreatingStocks] = useState(false);

  const [feedback, setFeedback] = useState<{ severity: 'success' | 'info' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (open) setTargetWarehouse(null);
  }, [open, sourceWarehouse?.id]);

  // Ekranda zaten seçili depolar liste dışı: aynı depo iki sekmede olamaz.
  const options = useMemo(
    () => allWarehouses.filter((warehouse) => !selectedWarehouseIds.includes(warehouse.id)),
    [allWarehouses, selectedWarehouseIds]
  );

  const handleConfirm = async () => {
    if (!sourceWarehouse || !targetWarehouse) return;

    setChanging(true);
    try {
      onConfirm(sourceWarehouse.id, targetWarehouse.id);
      onClose();

      if (productIds.length === 0) {
        setFeedback({
          severity: 'success',
          message: `Depo "${targetWarehouse.name}" olarak değiştirildi.`,
        });
        return;
      }

      // Kontrol, taşıma yapıldıktan SONRA: kullanıcı diyalogu kapatsa bile
      // seçimler yeni depoya geçmiş olur, sadece tanım açma adımı atlanır.
      let missing: MissingWarehouseProduct[] = [];
      try {
        missing = await findProductsMissingInWarehouse(productIds, targetWarehouse.id);
      } catch (e) {
        console.error('[WarehouseChangeDialog] Depo stok kontrolü başarısız:', e);
        setFeedback({
          severity: 'info',
          message:
            `Depo "${targetWarehouse.name}" olarak değiştirildi, ancak ürünlerin bu depoda tanımlı ` +
            'olup olmadığı kontrol edilemedi. Satışta eksik tanımlar otomatik açılır.',
        });
        return;
      }

      if (missing.length === 0) {
        setFeedback({
          severity: 'success',
          message: `Depo "${targetWarehouse.name}" olarak değiştirildi; ${productIds.length} ürünün tamamı bu depoda tanımlı.`,
        });
        return;
      }

      setMissingWarehouse(targetWarehouse);
      setMissingProducts(missing);
    } finally {
      setChanging(false);
    }
  };

  const handleCreateMissingStocks = async () => {
    if (!companyId || !missingWarehouse || !missingProducts?.length) return;
    setCreatingStocks(true);
    try {
      const count = await createProductsInWarehouse(
        companyId,
        missingWarehouse.id,
        missingProducts.map((product) => product.id)
      );
      setFeedback({
        severity: 'success',
        message: `${count} ürün "${missingWarehouse.name}" deposunda 1 adetle tanımlandı. Eski depodaki miktarlar değişmedi.`,
      });
      setMissingProducts(null);
      setMissingWarehouse(null);
    } catch (e: any) {
      setFeedback({
        severity: 'error',
        message:
          e?.response?.data?.message ||
          e?.message ||
          'Ürünler depoda tanımlanamadı. Depo/ürün modülünden manuel ekleyebilirsiniz.',
      });
    } finally {
      setCreatingStocks(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!changing) onClose();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon color="primary" />
          {sourceWarehouse ? `Depoyu Değiştir: ${sourceWarehouse.name}` : 'Depoyu Değiştir'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bu sekmedeki kategori ve ürün seçimleri seçtiğiniz depoya taşınır; hiçbir seçimi yeniden
            yapmanız gerekmez. Eski depo ekrandan çıkarılır, ürünlerin eski depodaki stok miktarları
            olduğu gibi kalır.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip size="small" label={`${categoryCount} kategori`} />
            <Chip size="small" label={`${productIds.length} ürün`} />
          </Stack>

          {options.length === 0 ? (
            <Alert severity="warning">
              Taşınabilecek başka depo yok. Ekranda seçili olmayan bir depo tanımlayın veya diğer depo
              sekmelerinden birini kaldırın.
            </Alert>
          ) : (
            <Autocomplete
              size="small"
              options={options}
              getOptionLabel={(option) => option.name}
              value={targetWarehouse}
              onChange={(_e, value) => setTargetWarehouse(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={changing}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Yeni depo"
                  helperText="Ekranda zaten seçili depolar listede görünmez."
                />
              )}
            />
          )}

          {changing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Ürünlerin yeni depoda tanımlı olup olmadığı kontrol ediliyor…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={changing}>
            İptal
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            variant="contained"
            disabled={!targetWarehouse || changing}
          >
            Depoyu Değiştir
          </Button>
        </DialogActions>
      </Dialog>

      <MissingWarehouseStockDialog
        open={Boolean(missingProducts?.length)}
        warehouseName={missingWarehouse?.name || ''}
        products={missingProducts || []}
        creating={creatingStocks}
        onSkip={() => {
          setMissingProducts(null);
          setMissingWarehouse(null);
        }}
        onCreate={() => void handleCreateMissingStocks()}
      />

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback?.severity || 'info'} onClose={() => setFeedback(null)}>
          {feedback?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WarehouseChangeDialog;
